/**
 * Plan cards shown on the billing surfaces (Plan & Usage, Compare Plans).
 *
 * Two cards: Enterprise (a real, sellable tier) and Max (marketing only, no
 * slug, contact sales). Both are annual commitments quoted at a monthly price,
 * so no billing-interval choice is offered on either surface.
 *
 * Numbers come from `@digital-twins/shared-types` (`pricing-constants.ts`),
 * which the backend mirrors in `apps/backend/src/billing/billing-config.service.ts`
 * (`HARDCODED_DEFAULTS`) and in the `pricing_plans.settings` rows seeded via the
 * launch-pricing migrations.
 *
 * The backend currently does NOT expose a public `GET /billing/plans` endpoint
 * that returns this data; until it does, everything hardcoded below (labels,
 * copy, the Max figures) MUST be kept in sync with the backend by hand. Any
 * price change in Lemon Squeezy that is mirrored to `pricing_plans.settings`
 * must also be reflected here. Tracked as a follow-up in
 * `docs/plans/launch-pricing.md` (Wave U1).
 */

import type { BillingInterval, SubscriptionTier } from '@/lib/api'
import {
  PLAN_PRICES,
  PLAN_CREDITS,
  OVERAGE_RATE_CENTS_MONTHLY,
  OVERAGE_RATE_CENTS_ANNUAL,
} from '@digital-twins/shared-types'

export type SubscribablePlan = Extract<SubscriptionTier, 'pro' | 'enterprise'>

/** Display name of the top card. Single rename point for the whole app. */
export const MAX_PLAN_NAME = 'Max'

/** Card identity. `max` is marketing only, so it has no subscription tier. */
export type PlanCardId = 'enterprise' | 'max'

export interface PlanCardConfig {
  id: PlanCardId
  /** Tier this card actually sells. `null` = marketing-only card. */
  tier: SubscribablePlan | null
  displayName: string
  logoSrc: string
  /** Price in dollars per month. `null` renders as "Custom". */
  monthlyPriceUsd: number | null
  /** Line under the price, e.g. 'billed annually'. Empty string renders nothing. */
  priceCaption: string
  /** Pre-formatted credits line, ready to render as-is. */
  creditsLabel: string
  /** Credits included per period. `null` when the card advertises no figure. */
  includedCredits: number | null
  /** Per-credit overage in cents. `null` when the card advertises no figure. */
  overageRateCentsMonthly: number | null
  shortDescription: string
  showUnlimitedSeatsPill: boolean
  /** True for cards whose CTA is "Contact sales" rather than a checkout. */
  contactSales: boolean
  /**
   * Where the "Contact sales" CTA sends the user. Defaults to the public
   * Fairgen contact form. Open in a new tab so the user doesn't lose their
   * in-app session / cart state.
   */
  contactSalesUrl?: string
}

/**
 * Public contact form. Used by every "Contact sales" CTA across billing
 * surfaces; centralized here so a future URL change (e.g. typeform, custom
 * Enterprise inquiry path) is a one-line edit.
 */
export const CONTACT_SALES_URL = 'https://www.fairgen.ai/forms/contact-us'

/**
 * Ordered left to right on both billing surfaces: Enterprise, then Max.
 *
 * Enterprise is custom priced on buying surfaces (`monthlyPriceUsd: null`
 * renders as "Custom"). Credits and overage still come from the shared pricing
 * constants (10,000 credits in a shared org pool, 25¢ per extra credit). Max is
 * also custom priced; its CTA is "Contact sales".
 */
export const PLAN_CARDS: PlanCardConfig[] = [
  {
    id: 'enterprise',
    tier: 'enterprise',
    displayName: 'Enterprise',
    logoSrc: '/billing/logo-plan-enterprise.svg',
    monthlyPriceUsd: null,
    priceCaption: '',
    creditsLabel: `${PLAN_CREDITS.enterprise.toLocaleString()} credits / org`,
    includedCredits: PLAN_CREDITS.enterprise,
    overageRateCentsMonthly: OVERAGE_RATE_CENTS_MONTHLY.enterprise,
    shortDescription: 'Shared credit pool · Unlimited seats · Priority support',
    showUnlimitedSeatsPill: true,
    contactSales: false,
  },
  {
    id: 'max',
    tier: null,
    displayName: MAX_PLAN_NAME,
    logoSrc: '/billing/logo-plan-max.svg',
    monthlyPriceUsd: null,
    priceCaption: '',
    creditsLabel: 'Additional 5,000 credits / category',
    includedCredits: null,
    overageRateCentsMonthly: null,
    shortDescription: 'Multiple categories · Enterprise adoption plan · SSO',
    showUnlimitedSeatsPill: true,
    contactSales: true,
  },
]

export function getPlanCard(id: PlanCardId): PlanCardConfig {
  const card = PLAN_CARDS.find((c) => c.id === id)
  if (!card) {
    throw new Error(`Unknown plan card: ${id}`)
  }
  return card
}

/**
 * Per-credit overage in cents for a tier on a given billing interval.
 *
 * Read straight from the shared constants rather than from `PLAN_CARDS`: the
 * cards only advertise the monthly rate, but the real-usage surfaces
 * (PlanSummaryCard, UsageLogTable, TeamUsageSection) call this with the org's
 * actual tier and billing interval, so an existing annual customer must keep
 * seeing the annual rate they are really charged. Marketing surfaces pass
 * 'monthly'. Unknown or unpriced tiers return 0.
 */
export function getOverageRateForPlan(
  tier: SubscriptionTier | null | undefined,
  interval: BillingInterval | null | undefined,
): number {
  if (!tier) return 0
  if (tier === 'pro' || tier === 'enterprise') {
    return interval === 'annual'
      ? OVERAGE_RATE_CENTS_ANNUAL[tier]
      : OVERAGE_RATE_CENTS_MONTHLY[tier]
  }
  return 0
}

/** Subscription price in dollars for a tier on a given billing interval. */
export function getPlanPriceUsd(
  tier: SubscribablePlan,
  interval: BillingInterval,
): number {
  const prices = PLAN_PRICES[tier]
  return interval === 'annual' ? prices.annualCents / 100 : prices.monthlyCents / 100
}
